import React, { FC, useCallback, useContext, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '@typings/redux';
import { limits } from 'config/pagination';
import { LocaleContext } from 'context/locale';
import { personalDataChangeRegisterListSelector } from 'store/reports/reports.selector';
import { fetchDataChangeRegisterList, clearStudentChildDataChangeRegister } from 'store/reports/reports.action';
import { focusField } from 'utils/form';
import { DataGrid } from 'components/shared/data/DataGrid/DataGrid';
import { Paper } from 'components/shared/surfaces/Paper/Paper';
import { ColumnsConfig } from 'components/shared/data/DataGrid/DataGrid.types';
import { useStyles } from 'components/childRegister/Editor/EducationPath/EducationPath.styles';
import { DataGridApi } from 'components/shared/data/DataGrid/DataGrid.types';
import { PersonalDataChanges, Origin, FieldName, FieldChange, DataChangeAction } from '../AccessList.types';
import { Toolbar } from './Toolbar';

export const StudentChildDataChangeRegisterList: FC = () => {
  const { trans } = useContext(LocaleContext);
  const dispatch = useDispatch<AppDispatch>();
  const { list, total, filters } = useSelector(personalDataChangeRegisterListSelector);
  const classes = useStyles();
  const toolbarRef = useRef(null);

  const [gridApi, setGridApi] = useState<DataGridApi>(null);

  useEffect(() => {
    dispatch(clearStudentChildDataChangeRegister());
  }, [dispatch]);

  useEffect(() => {
    if (gridApi) {
      gridApi.resetPagination();
    }
  }, [filters, gridApi]);

  const getOriginName = (origin: Origin) => {
    if (origin === Origin.CHILD_REGISTER) {
      return trans('PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.ORIGIN_NAME.CHILD_REGISTER');
    } else if (origin === Origin.STUDENTS) {
      return trans('PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.ORIGIN_NAME.STUDENTS');
    } else {
      return '';
    }
  };

  const getActionName = (actionName: DataChangeAction) => {
    return trans(`PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.DATA_CHANGE_ACTION_NAME.${actionName.toUpperCase()}`);
  };

  const getFieldName = (name: FieldName) => {
    return trans(`USER_PERSONAL_DATA_CHANGE_REGISTER_LIST.FIELD_NAME.${name.toUpperCase()}`);
  };

  const formatChangedFieldValue = (value: string | null, fieldName: FieldName) => {
    if (value === null || value === 'null') {
      return trans('USER_PERSONAL_DATA_CHANGE_REGISTER_LIST.CHANGED_FIELDS_VALUE.NULL');
    } else if (fieldName === FieldName.IDENTIFIER_TYPE) {
      return trans(
        `USER_PERSONAL_DATA_CHANGE_REGISTER_LIST.CHANGED_FIELDS_VALUE.IDENTIFIER_TYPE.${value.toUpperCase()}`,
      );
    } else if (fieldName === FieldName.SEX) {
      return trans(`USER_PERSONAL_DATA_CHANGE_REGISTER_LIST.CHANGED_FIELDS_VALUE.SEX.${value.toUpperCase()}`);
    } else {
      return value;
    }
  };

  const getChangedFieldsList = (changes: FieldChange[], actionName: DataChangeAction) => {
    if (
      changes.length > 0 &&
      (actionName === DataChangeAction.UPDATE_CHILD ||
        actionName === DataChangeAction.UPDATE_STUDENT ||
        actionName === DataChangeAction.UPDATE_CHILD_GUARDIAN)
    ) {
      const changedFields = [];
      changes.forEach((item, index) => {
        changedFields.push(
          ` ${getFieldName(item.fieldName)} (${formatChangedFieldValue(
            item.oldValue,
            item.fieldName,
          )} => ${formatChangedFieldValue(item.newValue, item.fieldName)})${index < changes.length - 1 ? '' : '.'}`,
        );
      });
      return `Zmieniono pola: ${changedFields.toString()}`;
    } else {
      return '';
    }
  };

  const getColumns: ColumnsConfig<PersonalDataChanges, void> = () => [
    {
      field: 'changeDate',
      name: 'PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.CHANGE_DATE',
      columnParams: { minWidth: 130 },
      format: (data, trans, formatter) => formatter.formatDateWithTime(data.changeDate),
    },
    {
      field: 'userLogin',
      name: 'PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.USER_LOGIN',
      format: (data) => `${data.userLogin} / ${data.userName}`,
    },
    {
      field: 'userIp',
      name: 'PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.USER_IP',
      columnParams: { minWidth: 110 },
      format: (data) => data.userIp,
    },
    {
      field: 'origin',
      name: 'PERSONAL_DATA_ACCESS.USER_PERSONAL_DATA_CHANGE_REGISTER.FILTER_TOOLBAR.STUDENT_ORIGIN.LABEL',
      format: (data) => getOriginName(data.origin),
    },
    {
      field: 'lastName',
      name: 'STUDENT_CHILD_DATA_CHANGE_REGISTER_LIST.COLUMNS.LAST_NAME',
      format: (data) => `${data.firstName} ${data.lastName}`,
    },
    {
      field: 'action',
      name: 'PERSONAL_DATA_ACCESS.DATA_CHANGE_REGISTER_LIST.COLUMNS.ACTION',
      columnParams: { flex: 1, minWidth: 280 },
      format: (data) => `${getActionName(data.action)} ${getChangedFieldsList(data.changesRange, data.action)}`,
    },
  ];

  const onDataFetch = useCallback((page) => dispatch(fetchDataChangeRegisterList(page, filters)), [dispatch, filters]);
  const onDataProvide = useCallback((page) => list[page], [list]);

  const onGridReadyHandler = useCallback(
    (grid: DataGridApi) => {
      setGridApi(grid);
    },
    [setGridApi],
  );

  const onFocusBeforeGridHandler = useCallback(() => {
    if (toolbarRef.current) {
      // using setTimeout to wait over the tab event that we can't prevent
      setTimeout(() => focusField(toolbarRef.current, 'studentRegister'));
    }
  }, [toolbarRef]);

  const onFocusAfterGridHandler = () => {
    setTimeout(() => document.getElementById('sidebar-button').focus());
  };

  const gridFocusHandler = () => {
    gridApi && gridApi.focusCell(0, 0);
  };

  return (
    <Paper innerSpacing={2} square={true} className={classes.container}>
      <Toolbar onGridFocus={gridFocusHandler} ref={toolbarRef} />
      <DataGrid<PersonalDataChanges>
        columns={getColumns()}
        onDataProvide={onDataProvide}
        onDataFetch={onDataFetch}
        total={total}
        limit={limits.personalDataAccessList}
        loadingMessage={'COMMON.LOADING_DATA'}
        noResultsMessage={'COMMON.NOTHING-FOUND'}
        onGridReady={onGridReadyHandler}
        onFocusBeforeGrid={onFocusBeforeGridHandler}
        onFocusAfterGrid={onFocusAfterGridHandler}
      />
    </Paper>
  );
};
